/**
 * StatWhyPanel — Slide-out side panel that explains a single hero
 * stat. Triggered when a user clicks any PageHero stat tile (or
 * the north-star tile) that was given a `whyPanel` prop.
 *
 * The panel renders four standard sections so every "Why?" feels
 * the same regardless of which page launched it:
 *   1. Headline value (large)
 *   2. How it's computed (plain-English formula or component
 *      breakdown — caller passes `breakdown: [{label, value, weight?, hint?}]`)
 *   3. Last 4 quarters mini-table (caller passes `history: [{label, value}]`)
 *   4. Sources / drill-in CTAs (caller passes `links: [{label, path}]`)
 *
 * All sections are optional; missing sections collapse silently
 * so simpler stats can ship a one-paragraph "why" without forcing
 * placeholder data.
 */
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../ui/sheet';
import { ArrowRight, Calculator, Clock, BookOpen, Sparkles } from 'lucide-react';

export function StatWhyPanel({ open, onClose, why }) {
  const navigate = useNavigate();

  // Esc key already closes via Sheet; nothing extra to wire.
  useEffect(() => {
    // Future: emit an analytics beacon "stat-why-opened" so we
    // can rank which stats users dig into the most. Punted for
    // now to keep the panel focused on rendering.
  }, [open, why]);

  if (!why) return null;

  const {
    label,
    value,
    valueSuffix,
    description,
    formula,
    breakdown,
    history,
    links,
  } = why;

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose?.()}>
      <SheetContent
        className="w-full sm:max-w-md md:max-w-lg overflow-y-auto"
        data-testid="stat-why-panel"
      >
        <SheetHeader className="text-left pb-4 border-b">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500">
            <Sparkles className="h-3.5 w-3.5" />
            Why this number?
          </div>
          <SheetTitle className="text-2xl mt-1" data-testid="stat-why-label">
            {label}
          </SheetTitle>
          <div className="flex items-baseline gap-2 mt-1">
            <span
              className="text-4xl font-extrabold text-slate-900 tracking-tight"
              data-testid="stat-why-value"
            >
              {value ?? '—'}
            </span>
            {valueSuffix && (
              <span className="text-lg font-semibold text-slate-500">
                {valueSuffix}
              </span>
            )}
          </div>
          {description && (
            <SheetDescription className="text-sm text-slate-600 mt-2 leading-relaxed">
              {description}
            </SheetDescription>
          )}
        </SheetHeader>

        {formula && (
          <section className="py-5 border-b" data-testid="stat-why-formula">
            <div className="flex items-center gap-2 mb-2">
              <Calculator className="h-3.5 w-3.5 text-indigo-600" />
              <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                How it&rsquo;s computed
              </h3>
            </div>
            <p className="text-sm text-slate-700 leading-relaxed">{formula}</p>
          </section>
        )}

        {Array.isArray(breakdown) && breakdown.length > 0 && (
          <section className="py-5 border-b" data-testid="stat-why-breakdown">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">
              Contributing components
            </h3>
            <ul className="space-y-2">
              {breakdown.map((b, i) => (
                <li
                  key={`${b.label}-${i}`}
                  className="flex items-baseline justify-between gap-3 text-sm"
                  data-testid={`stat-why-breakdown-row-${i}`}
                >
                  <div className="min-w-0">
                    <p className="font-medium text-slate-800 truncate">{b.label}</p>
                    {b.hint && (
                      <p className="text-xs text-slate-500 truncate">{b.hint}</p>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-semibold tabular-nums text-slate-900">
                      {b.value ?? '—'}
                    </p>
                    {typeof b.weight === 'number' && (
                      <p className="text-[10px] uppercase tracking-wider text-slate-400">
                        weight {Math.round(b.weight * 100)}%
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {Array.isArray(history) && history.length > 0 && (
          <section className="py-5 border-b" data-testid="stat-why-history">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-3.5 w-3.5 text-indigo-600" />
              <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                Last {history.length} quarters
              </h3>
            </div>
            <table className="w-full text-sm">
              <tbody>
                {history.map((h, i) => (
                  <tr
                    key={`${h.label}-${i}`}
                    className="border-b last:border-b-0 border-slate-100"
                    data-testid={`stat-why-history-row-${i}`}
                  >
                    <td className="py-1.5 text-slate-600">{h.label}</td>
                    <td className="py-1.5 text-right font-semibold tabular-nums text-slate-900">
                      {h.value ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {Array.isArray(links) && links.length > 0 && (
          <section className="py-5" data-testid="stat-why-links">
            <div className="flex items-center gap-2 mb-3">
              <BookOpen className="h-3.5 w-3.5 text-indigo-600" />
              <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                Drill in
              </h3>
            </div>
            <ul className="space-y-1.5">
              {links.map((l, i) => (
                <li key={`${l.path}-${i}`}>
                  <button
                    type="button"
                    onClick={() => {
                      onClose?.();
                      if (l.path) navigate(l.path);
                    }}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors text-left text-sm"
                    data-testid={`stat-why-link-${i}`}
                  >
                    <span className="text-slate-700 group-hover:text-indigo-700 truncate">
                      {l.label}
                    </span>
                    <ArrowRight className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default StatWhyPanel;
